import { normalizeLocale } from '@/i18n/locale';
import type { Locale } from '@/i18n/translations';
import { supabase } from '../SupabaseClient/supabase-client';

export type CurrentUserInfo = Record<string, unknown>;

const normalizeUserInfo = (data: unknown): CurrentUserInfo => {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as CurrentUserInfo;
  }

  return { raw: data ?? null };
};

export const userService = {
  async getCurrentUser(): Promise<CurrentUserInfo> {
    const { data, error } = await supabase.functions.invoke('me');
    if (error) {
      throw error;
    }

    const normalized = normalizeUserInfo(data);
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) {
      return normalized;
    }

    const metadataLocale =
      typeof authData.user?.user_metadata?.locale === 'string'
        ? authData.user?.user_metadata?.locale
        : null;
    const normalizedLocale =
      typeof normalized.locale === 'string'
        ? normalizeLocale(normalized.locale) ?? normalized.locale
        : normalizeLocale(metadataLocale);

    return normalizeUserInfo({
      ...normalized,
      locale: normalizedLocale ?? null,
    });
  },

  async updateName(name: string): Promise<CurrentUserInfo> {
    const trimmedName = name.trim();
    const { data, error } = await supabase.auth.updateUser({
      data: { full_name: trimmedName },
    });

    if (error) {
      throw error;
    }

    return normalizeUserInfo({
      name: data.user?.user_metadata?.full_name ?? trimmedName,
      email: data.user?.email ?? null,
    });
  },

  async updateLocale(locale: Locale): Promise<CurrentUserInfo> {
    const { data, error } = await supabase.auth.updateUser({
      data: { locale },
    });

    if (error) {
      throw error;
    }

    return normalizeUserInfo({
      locale: data.user?.user_metadata?.locale ?? locale,
    });
  },
};
